import {
  AddItemSetQuestionConditionsModal,
  AddSingleQuestionConditionsModal,
  UpdateItemSetQuestionConditionsModal,
  UpdateSingleQuestionConditionsModal,
} from '@exam-management/qconfig-question-conditions';
import type { PropsWithChildren } from '@shared/utils';
import { createContext, useCallback, useMemo, useState } from 'react';

import { DeleteQConfigStructureModal } from '../../containers';

import type {
  QConfigStructureModalAction,
  QConfigStructureContextData,
  ModalData,
  SetActionArgs,
  QConfigDeleteArgs,
  AddSingleQuestionConditionsArgs,
  UpdateSingleQuestionConditionsArgs,
  AddItemSetQuestionConditionsArgs,
  UpdateItemSetQuestionConditionsArgs,
} from './types';

const QConfigStructureContext = createContext<QConfigStructureContextData | undefined>(undefined);

function QConfigStructureModalProvider(props: Readonly<PropsWithChildren>) {
  const { children } = props;
  const [modalData, setModalData] = useState<ModalData | null>(null);
  const [action, setAction] = useState<QConfigStructureModalAction | null>(null);

  const handleSetAction = useCallback(
    (args: SetActionArgs) => {
      setAction(args.action);
      setModalData(args?.payload ?? null);
    },
    [setAction, setModalData],
  );

  const handleClose = useCallback(() => {
    setAction(null);
    setModalData(null);
  }, [setAction, setModalData]);

  const contextValue = useMemo(
    () => ({
      setAction: handleSetAction,
      close: handleClose,
    }),
    [handleSetAction, handleClose],
  );

  return (
    <QConfigStructureContext.Provider value={contextValue}>
      {children}
      {action === 'QCONFIG_DELETE' ? (
        <DeleteQConfigStructureModal
          open={action === 'QCONFIG_DELETE'}
          onClose={handleClose}
          {...(modalData as QConfigDeleteArgs)}
        />
      ) : null}
      {action === 'ADD_SINGLE_QUESTION_CONDITIONS' ? (
        <AddSingleQuestionConditionsModal
          open={action === 'ADD_SINGLE_QUESTION_CONDITIONS'}
          onClose={handleClose}
          {...(modalData as AddSingleQuestionConditionsArgs)}
        />
      ) : null}
      {action === 'UPDATE_SINGLE_QUESTION_CONDITIONS' ? (
        <UpdateSingleQuestionConditionsModal
          open={action === 'UPDATE_SINGLE_QUESTION_CONDITIONS'}
          onClose={handleClose}
          {...(modalData as UpdateSingleQuestionConditionsArgs)}
        />
      ) : null}
      {action === 'ADD_ITEM_SET_QUESTION_CONDITIONS' ? (
        <AddItemSetQuestionConditionsModal
          open={action === 'ADD_ITEM_SET_QUESTION_CONDITIONS'}
          onClose={handleClose}
          {...(modalData as AddItemSetQuestionConditionsArgs)}
        />
      ) : null}
      {action === 'UPDATE_ITEM_SET_QUESTION_CONDITIONS' ? (
        <UpdateItemSetQuestionConditionsModal
          open={action === 'UPDATE_ITEM_SET_QUESTION_CONDITIONS'}
          onClose={handleClose}
          {...(modalData as UpdateItemSetQuestionConditionsArgs)}
        />
      ) : null}
    </QConfigStructureContext.Provider>
  );
}

export { QConfigStructureContext, QConfigStructureModalProvider };
